<app-page-shell [breadcrumb]="breadcrumb" width="wide">
  <header class="mx-auto max-w-3xl px-6 pt-6 pb-10 text-center md:pt-16 md:pb-20">
    <p class="mb-4 eyebrow">{{ 'pages.questionCloud.hero.eyebrow' | transloco }}</p>
    <h1 class="hero-title font-display text-4xl font-bold leading-[1.06] text-foreground md:text-[3.25rem]">
      <span>{{ 'pages.questionCloud.hero.titlePrefix' | transloco }}</span>
      <span class="hero-title-accent italic font-medium text-primary">{{ 'pages.questionCloud.hero.titleAccent' | transloco }}</span>
    </h1>
    <p class="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-[17px]">
      {{ 'pages.questionCloud.hero.description' | transloco }}
    </p>
  </header>

  <!-- Busca -->
  <div
    class="mx-auto mt-10 flex max-w-xl items-center gap-2 rounded-full border border-border bg-card px-4 py-2.5 transition focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/30"
  >
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-muted-foreground">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
    <input
      #searchInput
      type="text"
      role="searchbox"
      [value]="query()"
      (input)="onSearch(searchInput.value)"
      [attr.placeholder]="'pages.questionCloud.searchPlaceholder' | transloco"
      [attr.aria-label]="'pages.questionCloud.searchPlaceholder' | transloco"
      class="input-unstyled min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 text-sm text-foreground shadow-none outline-none ring-0 placeholder:text-muted-foreground/70 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:shadow-none"
    />
    @if (query()) {
      <button
        type="button"
        (click)="onSearch(''); searchInput.value = ''"
        [attr.aria-label]="'pages.questionCloud.clearSearch' | transloco"
        class="rounded-full p-1 text-muted-foreground transition hover:text-foreground"
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5">
          <path d="M18 6 6 18" /><path d="m6 6 12 12" />
        </svg>
      </button>
    }
  </div>

  <!-- Chips de plataforma -->
  <div class="mx-auto mt-8 flex max-w-4xl flex-wrap items-center justify-center gap-2">
    <button
      type="button"
      (click)="setActive(null)"
      [attr.aria-pressed]="active() === null"
      class="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition"
      [class.border-primary]="active() === null"
      [class.bg-primary]="active() === null"
      [class.text-primary-foreground]="active() === null"
      [class.border-border]="active() !== null"
      [class.bg-card]="active() !== null"
      [class.text-muted-foreground]="active() !== null"
    >
      {{ 'pages.questionCloud.allPlatforms' | transloco }}
      <span class="text-[10px] opacity-70 tabular-nums">{{ totalCount() }}</span>
    </button>

    @for (platform of platforms(); track platform.name) {
      <button
        type="button"
        (click)="setActive(platform.name)"
        [attr.aria-pressed]="active() === platform.name"
        class="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition"
        [class.border-primary]="active() === platform.name"
        [class.bg-primary]="active() === platform.name"
        [class.text-primary-foreground]="active() === platform.name"
        [class.border-border]="active() !== platform.name"
        [class.bg-card]="active() !== platform.name"
        [class.text-muted-foreground]="active() !== platform.name"
      >
        {{ platform.name }}
        <span class="text-[10px] opacity-70 tabular-nums">{{ platform.count }}</span>
      </button>
    }
  </div>

  <!-- Toolbar + grade anchor (pagination scrolls here) -->
  <div #questionGrid class="scroll-mt-24">
  <!-- Toolbar -->
  <div class="mx-auto mt-12 flex max-w-6xl items-center justify-between border-b border-border pb-3">
    <p class="text-sm text-muted-foreground">
      <span class="font-semibold text-foreground">{{ filtered().length }}</span>
      {{
        (filtered().length === 1
          ? 'pages.questionCloud.questionSingular'
          : 'pages.questionCloud.questionPlural'
        ) | transloco
      }}
    </p>
    <div class="flex items-center gap-2 text-xs text-muted-foreground">
      <span>{{ 'pages.questionCloud.sortLabel' | transloco }}</span>
      <app-editorial-select
        [options]="sortOptions()"
        [value]="sort()"
        [ariaLabel]="'pages.questionCloud.sortLabel' | transloco"
        align="right"
        (valueChange)="onSort($event)"
      />
    </div>
  </div>

  <!-- Grade -->
  @if (loading()) {
    <div class="mx-auto mt-6 grid max-w-6xl gap-px overflow-hidden rounded-lg border border-border bg-border md:grid-cols-2 lg:grid-cols-3">
      @for (i of [1, 2, 3, 4, 5, 6]; track i) {
        <div class="h-36 animate-pulse bg-background"></div>
      }
    </div>
  } @else {
    <div
      class="mx-auto mt-6 grid max-w-6xl gap-px overflow-hidden rounded-lg border border-border bg-border md:grid-cols-2 lg:grid-cols-3"
    >
      @if (filtered().length === 0) {
        <div class="col-span-full bg-background p-10 text-center">
          <p class="font-display text-base font-semibold text-foreground">
            {{ 'pages.questionCloud.emptyTitle' | transloco }}
          </p>
          <p class="mt-1 text-sm text-muted-foreground">
            {{ 'pages.questionCloud.emptyDescription' | transloco }}
          </p>
        </div>
      } @else {
        @for (card of visibleCards(); track card.id) {
          <a
            [routerLink]="'/perguntas/' + card.perguntaId | localizedRouterLink"
            [queryParams]="card.queryParams"
            class="group flex h-full flex-col justify-between gap-6 bg-background p-6 transition hover:bg-primary/[0.04]"
          >
            <div>
              <p class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                {{ card.platform }}
              </p>
              <h3 class="mt-2 font-display text-base font-semibold leading-snug text-foreground transition-colors group-hover:text-primary">
                {{ card.short }}
              </h3>
            </div>
            <div class="flex items-center justify-between text-xs text-muted-foreground">
              <span class="inline-flex items-center gap-1 tabular-nums">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3">
                  <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                {{ card.views }}
              </span>
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5 -translate-x-1 opacity-0 transition group-hover:translate-x-0 group-hover:opacity-100 group-hover:text-primary">
                <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
              </svg>
            </div>
          </a>
        }
      }
    </div>

    <!-- Paginação (desktop) -->
    @if (!isMobile() && filtered().length > pageSize) {
      <nav
        class="mx-auto mt-8 flex max-w-6xl items-center justify-between gap-4"
        [attr.aria-label]="'pages.questionCloud.pagination.label' | transloco"
      >
        <button
          type="button"
          (click)="prevPage()"
          [disabled]="page() === 1"
          class="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-muted-foreground transition hover:border-primary/40 hover:text-primary disabled:opacity-40 disabled:hover:border-border disabled:hover:text-muted-foreground"
        >
          <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4">
            <path d="m15 18-6-6 6-6" />
          </svg>
          {{ 'pages.questionCloud.pagination.previous' | transloco }}
        </button>

        <span class="text-sm text-muted-foreground tabular-nums">
          {{ 'pages.questionCloud.pagination.page' | transloco }} {{ page() }} / {{ totalPages() }}
        </span>

        <button
          type="button"
          (click)="nextPage()"
          [disabled]="page() === totalPages()"
          class="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-muted-foreground transition hover:border-primary/40 hover:text-primary disabled:opacity-40 disabled:hover:border-border disabled:hover:text-muted-foreground"
        >
          {{ 'pages.questionCloud.pagination.next' | transloco }}
          <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4">
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>
      </nav>
    }

    <!-- Sentinela do infinite scroll (mobile) -->
    <div #loadMore class="h-px w-full"></div>
    @if (hasMoreMobile()) {
      <div class="mt-6 flex justify-center">
        <span class="h-6 w-6 animate-spin rounded-full border-2 border-primary/30 border-t-primary"></span>
      </div>
    }
  }
  </div>

  <app-promo-banner
    eyebrowKey="pages.questionCloud.promo.eyebrow"
    titlePrefixKey="pages.questionCloud.promo.titlePrefix"
    titleAccentKey="pages.questionCloud.promo.titleAccent"
    descriptionKey="pages.questionCloud.promo.description"
    ctaLabelKey="pages.questionCloud.promo.cta"
    ctaPath="/igualdade-racial"
  />
</app-page-shell>
